/**
 * Templates API
 *
 * Query, create, update, validate, and manage template resources. Includes
 * both authenticated endpoints and a public list endpoint via `SERVER_URL`.
 */
import { get, post, put, del, SERVER_URL } from '../core';

/**
 * Fetches public templates without requiring authentication
 * @returns {Promise<Array>} - List of public templates with minimal information
 */
export function getPublicTemplates() {
  // Using the external API endpoint instead of internal API
  return fetch(`${SERVER_URL}/publicTemplates`)
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    });
}

/**
 * Fetches templates for the current namespace
 * @param {Object} options - Additional options for the request
 * @returns {Promise<Array>} - The list of templates
 */
export function getTemplates(options = {}) {
  const {
    page = 1,
    limit = 25,
    tags = '',
    search = '',
    searchType = 'random',
    type = '',
    showSavedTemplates = false,
    namespaceId = null
  } = options;

  // Create proper URL with query parameters
  const queryParams = new URLSearchParams();
  queryParams.append('page', page);
  queryParams.append('limit', limit);
  if (tags) queryParams.append('tags', tags);
  if (search) queryParams.append('search', search);
  if (type) queryParams.append('type', type);
  queryParams.append('searchType', searchType);
  if (showSavedTemplates) queryParams.append('showSavedTemplates', 'true');
  if (namespaceId && showSavedTemplates) queryParams.append('namespaceId', namespaceId);
  
  // Pass a doubled timeout (20 seconds) for templates endpoint specifically
  return get(`/templates?${queryParams.toString()}`, { timeout: 20000 });
}

/**
 * Fetches a single template by ID
 * @param {string} namespaceId - The ID of the namespace
 * @param {string} templateId - The ID of the template
 * @param {Object} options - Additional options for the request
 * @returns {Promise<Object>} - The template data
 */
export function getTemplate(namespaceId, templateId, options = {}) {
  return get(`/namespaces/${namespaceId}/templates/${templateId}`, options);
}

/**
 * Creates a new template in the specified namespace
 * @param {string} namespaceId - The ID of the namespace
 * @param {Object} templateData - The template data to create
 * @returns {Promise<Object>} - The created template
 */
export function createTemplate(namespaceId, templateData) {
  return post(`/namespaces/${namespaceId}/templates`, templateData);
}

/**
 * Updates an existing template
 * @param {string} namespaceId - The ID of the namespace
 * @param {string} templateId - The ID of the template to update
 * @param {Object} templateData - The updated template data
 * @returns {Promise<Object>} - The updated template
 */
export function updateTemplate(namespaceId, templateId, templateData) {
  return put(`/namespaces/${namespaceId}/templates/${templateId}`, templateData);
}

/**
 * Deletes a template
 * @param {string} namespaceId - The ID of the namespace
 * @param {string} templateId - The ID of the template to delete
 * @returns {Promise<Object>} - The response
 */
export function deleteTemplate(namespaceId, templateId) {
  return del(`/namespaces/${namespaceId}/templates/${templateId}`);
}

/**
 * Fetches template variables
 * @param {string} namespaceId - The ID of the namespace
 * @param {string} templateId - The ID of the template
 * @returns {Promise<Array>} - The template variables
 */
export function getTemplateVariables(namespaceId, templateId) {
  return get(`/namespaces/${namespaceId}/templates/${templateId}/variables`);
}

/**
 * Validates a template
 * @param {string} namespaceId - The ID of the namespace
 * @param {Object} templateData - The template data to validate
 * @returns {Promise<Object>} - The validation result
 */
export function validateTemplate(namespaceId, templateData) {
  return post(`/namespaces/${namespaceId}/templates/validate`, templateData);
}

/**
 * Fetches template statistics
 * @param {string} namespaceId - The ID of the namespace
 * @param {string} templateId - The ID of the template
 * @returns {Promise<Object>} - The template statistics
 */
export function getTemplateStats(namespaceId, templateId) {
  return get(`/namespaces/${namespaceId}/templates/${templateId}/stats`);
}

/**
 * Duplicates an existing template
 * @param {string} namespaceId - The ID of the namespace
 * @param {string} templateId - The ID of the template to duplicate
 * @param {Object} options - Options for duplication (like new name)
 * @returns {Promise<Object>} - The new duplicated template
 */
export function duplicateTemplate(namespaceId, templateId, options = {}) {
  return post(`/namespaces/${namespaceId}/templates/${templateId}/duplicate`, options);
}

/**
 * Fetches mail templates specifically
 * @param {Object} options - Additional options for the request
 * @returns {Promise<Array>} - The list of mail templates
 */
export function getMailTemplates(options = {}) {
  const {
    page = 1,
    limit = 25,
    tags = '',
    search = '',
    searchType = 'random',
    showSavedTemplates = false,
    namespaceId = null
  } = options;

  // Create proper URL with query parameters - type is automatically set to 'mail'
  const queryParams = new URLSearchParams();
  queryParams.append('page', page);
  queryParams.append('limit', limit);
  if (tags) queryParams.append('tags', tags);
  if (search) queryParams.append('search', search);
  queryParams.append('searchType', searchType);
  if (showSavedTemplates) queryParams.append('showSavedTemplates', 'true');
  if (namespaceId && showSavedTemplates) queryParams.append('namespaceId', namespaceId);
  
  // Use the specialized mail templates endpoint
  return get(`/templates/mail?${queryParams.toString()}`, { timeout: 20000 });
}

export default {
  getTemplates,
  getMailTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  getTemplateVariables,
  validateTemplate,
  getTemplateStats,
  duplicateTemplate,
  getPublicTemplates
}; 